/**
 * GH-268 — TourOverlay.
 *
 * Portal-rendered fixed layer. When `state === 'running'`, finds the active
 * step's anchor element via `document.querySelector` and:
 *   1. Smoothly scrolls it into view (block: 'center') so the spotlight
 *      lands on something visible — fixes the "anchor is below the fold"
 *      blank-spot bug.
 *   2. Renders a soft <TourSpotlight/> dim+cutout (NOT a blanket blur — the
 *      rest of the page stays legible) with an animated glow ring + pulse.
 *   3. Renders a persistent <TourAnchorMarker/> ✓ badge on every previously
 *      visited step's anchor (so the user can see what they've already
 *      covered without losing context).
 *   4. Positions <TourStep/> beside the anchor.
 *
 * Behaviors:
 *   - Polls for the anchor for up to 2s after mount/route change before
 *     gracefully advancing (some routes lazy-mount their content).
 *   - ESC key calls `skip()`.
 *   - Reposition on window resize/scroll (visited markers reposition too).
 */

import { useEffect, useState, useLayoutEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTour } from './use-tour'
import { TourStep } from './tour-step'
import { TourSpotlight, type SpotlightRect } from './tour-spotlight'
import { TourAnchorMarker } from './tour-anchor-marker'
import { TOUR_STEPS, type TourStepId } from './tour-step-config'
import { useAppStore } from '../../stores/app-store'
import { useTranslation } from 'react-i18next'

const POPOVER_GAP = 14
const ANCHOR_POLL_INTERVAL_MS = 100
const ANCHOR_POLL_TIMEOUT_MS = 1500

function computePopoverPosition(rect: SpotlightRect): { top: number; left: number } {
  // Default placement: below the anchor, left-aligned. Clamp to viewport.
  const popoverWidth = 380
  const popoverHeightEstimate = 200
  let top = rect.top + rect.height + POPOVER_GAP
  let left = rect.left

  if (typeof window !== 'undefined') {
    if (left + popoverWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - popoverWidth - 8)
    }
    if (top + popoverHeightEstimate > window.innerHeight - 8) {
      // Flip above
      top = Math.max(8, rect.top - popoverHeightEstimate - POPOVER_GAP)
    }
  }
  return { top, left }
}

export function TourOverlay() {
  const { state, currentStep, currentStepIndex, visitedSteps, skip } = useTour()
  const { t } = useTranslation('onboarding')
  const theme = useAppStore((s) => s.theme)
  const [anchorRect, setAnchorRect] = useState<SpotlightRect | null>(null)
  // True when the active step's anchor selector did not resolve within the
  // poll timeout. We render the popover at viewport center with a fallback
  // message instead of auto-advancing (which used to surprise the user by
  // changing routes without input).
  const [isAnchorMissing, setIsAnchorMissing] = useState(false)
  // Map of visited stepId → its current bounding rect. Repositioned on the
  // same resize/scroll listeners as the active anchor so markers stay glued.
  const [visitedRects, setVisitedRects] = useState<Record<string, SpotlightRect>>({})

  // Build the list of "visited but not currently active" stepIds we need to
  // render markers for. The active step gets the spotlight, not a marker.
  const markerStepIds: TourStepId[] = useMemo(
    () => visitedSteps.filter((id) => id !== currentStep?.id),
    [visitedSteps, currentStep],
  )

  // Poll for the active anchor element after each step change / state change.
  useEffect(() => {
    if (state !== 'running' || !currentStep) {
      setAnchorRect(null)
      setIsAnchorMissing(false)
      return
    }

    let cancelled = false
    const start = Date.now()
    setIsAnchorMissing(false)

    function tryFind() {
      if (cancelled) return
      const el = document.querySelector(currentStep!.selector) as HTMLElement | null
      if (el) {
        // Smooth-scroll the anchor into view BEFORE measuring, so the
        // spotlight animates to the post-scroll position and the user
        // visually follows along.
        try {
          const prefersReducedMotion =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          el.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'center',
            inline: 'nearest',
          })
        } catch {
          // older browsers — fall back silently
        }
        // Measure on next frame so we get the post-scroll rect.
        window.requestAnimationFrame(() => {
          if (cancelled) return
          const r = el.getBoundingClientRect()
          setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height })
        })
        return
      }
      if (Date.now() - start > ANCHOR_POLL_TIMEOUT_MS) {
        // Anchor never showed up — render the popover at viewport center with a
        // fallback message rather than auto-advancing. Auto-advancing previously
        // caused surprise route changes (steps 7 + 8 jumping into pages without
        // any user click). The user controls progression via the Next button.
        setAnchorRect(null)
        setIsAnchorMissing(true)
        return
      }
      window.setTimeout(tryFind, ANCHOR_POLL_INTERVAL_MS)
    }

    tryFind()
    return () => {
      cancelled = true
    }
  }, [state, currentStep])

  // Reposition the active anchor + all visited markers on resize/scroll.
  useLayoutEffect(() => {
    if (state !== 'running') return

    function reposition() {
      if (currentStep) {
        const el = document.querySelector(currentStep.selector) as HTMLElement | null
        if (el) {
          const r = el.getBoundingClientRect()
          setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height })
        }
      }
      // Recompute visited markers too.
      const nextRects: Record<string, SpotlightRect> = {}
      for (const id of markerStepIds) {
        const cfg = TOUR_STEPS.find((s) => s.id === id)
        if (!cfg) continue
        const el = document.querySelector(cfg.selector) as HTMLElement | null
        if (!el) continue
        const r = el.getBoundingClientRect()
        nextRects[id] = { top: r.top, left: r.left, width: r.width, height: r.height }
      }
      setVisitedRects(nextRects)
    }

    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [state, currentStep, markerStepIds])

  // ESC to skip.
  useEffect(() => {
    if (state !== 'running') return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') skip()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state, skip])

  if (state !== 'running' || !currentStep || typeof document === 'undefined') return null

  // If the anchor isn't available yet (still polling) we render nothing — once
  // it resolves OR the timeout fires (-> isAnchorMissing=true) we render at
  // viewport center.
  const popoverWidth = 380
  const popoverHeightEstimate = 200
  let pos: { top: number; left: number } | null = null
  if (anchorRect) {
    pos = computePopoverPosition(anchorRect)
  } else if (isAnchorMissing && typeof window !== 'undefined') {
    pos = {
      top: Math.max(8, Math.round((window.innerHeight - popoverHeightEstimate) / 2)),
      left: Math.max(8, Math.round((window.innerWidth - popoverWidth) / 2)),
    }
  }
  const stepKey = `${currentStep.id}-${currentStepIndex ?? 0}`
  const fallbackMessage = isAnchorMissing
    ? t('tour.fallback.anchorMissing', {
        defaultValue:
          "We couldn't pin this exactly — click Next when you're ready to continue.",
      })
    : null

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[60]" aria-hidden={false}>
      {/* Soft spotlight (NO blur — backdrop is a translucent dim with a
          feathered cutout around the active anchor). */}
      <TourSpotlight rect={anchorRect} stepKey={stepKey} theme={theme} />

      {/* Persistent visited-step markers — small ✓ badges on each previously
          covered anchor. Reposition with the same listeners as the active rect. */}
      {markerStepIds.map((id) => {
        const r = visitedRects[id]
        if (!r) return null
        return <TourAnchorMarker key={id} rect={r} />
      })}

      {/* Popover */}
      {pos && (
        <div
          className="pointer-events-auto absolute"
          style={{
            top: pos.top,
            left: pos.left,
            transition:
              'top 360ms cubic-bezier(0.16, 1, 0.3, 1), left 360ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <TourStep fallbackMessage={fallbackMessage} />
        </div>
      )}
    </div>,
    document.body,
  )
}
